import {
    serverTimestamp,
    DocumentData,
    QueryDocumentSnapshot,
    OrderByDirection,
    FirestoreDataConverter,
    QueryConstraint,
    Timestamp,
} from 'firebase/firestore';
export interface Change {
    field: string;
    before: string | null;
    after: string | null;
    beforeValues: string[] | null;
    afterValues: string[] | null;
}

export class BaseModel {
    __type = 'BaseModel';
    name: string;
    description: string | null;
    checked?: boolean;
    tenantId: string;
    constructor(data: any = {}) {
        this.name = data.name || '';
        this.description = data.description || null;
        this.checked = data.checked;
        this.tenantId = data.tenantId;
    }
    [key: string]: any;
}

export class Base extends BaseModel {
    __type = 'Base';
    id: string;
    createdAt: Timestamp | undefined;
    constructor(data: any = {}) {
        super(data);
        this.id = data.id;
        this.createdAt = data.createdAt;
    }
    indexId() {
        return this.id ? `${this.tenantId}_${this.id}` : '';
    }
    makeChanges = (_previous: Base | undefined, fields: string[]): Change[] => {
        const placeholder: { [key: string]: any } = {};
        const previous = _previous || placeholder;
        const changes: (Change | undefined)[] = fields.map((field) => {
            if (!this[field] && !previous[field]) {
                return undefined;
            }
            if (this[field] !== previous[field]) {
                return {
                    field,
                    before: previous[field] || null,
                    after: this[field],
                    beforeValues: null,
                    afterValues: null,
                };
            }
            return undefined;
        });
        return changes.filter((i) => !!i) as Change[];
    };
    makeArrayChanges = (_previous: Base | undefined, fields: string[]): Change[] => {
        const placeholder: { [key: string]: any } = {};
        const previous = _previous || placeholder;
        const changes: (Change | undefined)[] = fields.map((field) => {
            if (!this[field] && !previous[field]) {
                return undefined;
            }
            if (JSON.stringify(this[field]) !== JSON.stringify(previous[field])) {
                return {
                    field,
                    before: null,
                    after: null,
                    beforeValues: previous[field] || null,
                    afterValues: this[field],
                };
            }
            return undefined;
        });
        return changes.filter((i) => !!i) as Change[];
    };
    baseDiff(previous?: Base): Change[] {
        return this.makeChanges(previous, ['name', 'description']);
    }
    diff(previous?: Base): Change[] {
        return this.baseDiff(previous);
    }
    baseToFirestore() {
        const { name, description, tenantId } = this;
        if (!tenantId) {
            throw new Error('Non-empty tenantId is is mandatory');
        }
        return {
            name,
            description,
            tenantId,
            indexId: this.indexId(),
            indexName: `${this.tenantId}_${this.name.toLowerCase()}`,
            ...(this.createdAt ? {} : { createdAt: serverTimestamp() }),
        };
    }
    toFirestore() {
        return this.baseToFirestore();
    }
}

export class Language extends Base {
    __type: string = 'Language';
}

export class Country extends Base {
    __type: string = 'Country';
}

export class ProductType extends Base {
    __type: string = 'ProductType';
}

export class Clause extends Base {
    __type: Translatable = 'Clause';
    sectionIndexId: string;
    productTypeIndexIds: string[];
    index: number;
    clauseIndex: number;
    reviewStatus: 'approved' | 'sent' | 'returned' | '';
    reviewedBy: string[];
    reviewers: string[];
    content: string;
    constructor(data: any = {}) {
        super(data);
        this.sectionIndexId = data.sectionIndexId || 0;
        this.productTypeIndexIds = data.productTypeIndexIds || [];
        this.index = data.index || 0;
        this.clauseIndex = data.clauseIndex || 0;
        this.content = data.content || '';
        this.reviewStatus = data.reviewStatus || '';
        this.reviewedBy = data.reviewedBy || [];
        this.reviewers = data.reviewers || [];
    }
    diff(previous?: Base) {
        return this.baseDiff(previous)
            .concat(this.makeChanges(previous, ['sectionIndexId', 'content']))
            .concat(this.makeArrayChanges(previous, ['productTypeIndexIds']));
    }
    toFirestore() {
        const {
            content,
            sectionIndexId,
            productTypeIndexIds,
            reviewStatus,
            reviewedBy,
            reviewers,
        } = this;
        const d = {
            ...this.baseToFirestore(),
            content,
            sectionIndexId,
            productTypeIndexIds,
            reviewStatus,
            reviewedBy,
            reviewers,
        };
        console.log('Clause toFirestore', d);
        return d;
    }
}

export class Subsection extends Base {
    __type: Translatable = 'Subsection';
    sectionIndexId: string;
    content: string;
    clauses: Clause[];
    constructor(data: any = {}) {
        super(data);
        this.sectionIndexId = data.sectionIndexId || 0;
        this.clauses = (data.clauses || []).map((c: any) => new Clause(c));
        this.content = data.content || '';
    }
    toFirestore() {
        const { content, sectionIndexId } = this;
        return {
            ...this.baseToFirestore(),
            content,
            sectionIndexId,
        };
    }
}

export class Coverage extends Base {
    __type: Translatable = 'Coverage';
    productTypeIndexIds: string[];
    content: string;
    clauses: Clause[];
    constructor(data: any = {}) {
        super(data);
        this.productTypeIndexIds = data.productTypeIndexIds || [];
        this.content = data.content || '';
        this.clauses = (data.clauses || []).map((c: any) => new Clause(c));
    }
    toFirestore() {
        const { content, productTypeIndexIds } = this;
        return {
            ...this.baseToFirestore(),
            content,
            productTypeIndexIds,
        };
    }
}

export class Section extends Base {
    __type: Translatable = 'Section';
    expanded: boolean;
    clausesExpanded?: boolean;
    subsectionsExpanded?: boolean;
    coverage: boolean;
    clauses: Clause[];
    subsectionIndexIds: string[];
    selectedClauses: Clause[];
    constructor(data: any = {}) {
        super(data);
        this.coverage = !!data.coverage;
        this.expanded = !!data.expanded;
        this.clauses = data.clauses || [];
        this.selectedClauses = data.selectedClauses || [];
        this.subsectionIndexIds = data.subsectionIndexIds || [];
        this.clausesExpanded = true;
    }
    toFirestore() {
        const { coverage, subsectionIndexIds } = this;
        return {
            ...this.baseToFirestore(),
            coverage,
            subsectionIndexIds,
        };
    }
}
export interface Market {
    languageIndexId: string;
    countryIndexId: string;
    main?: boolean;
}

export interface Identifier {
    indexId: string;
    type: 'Clause' | 'Inner' | 'Coverage' | 'Subsection';
    segmentIndex: number;
}

export class Segment {
    __type: string = 'Segment';
    clauseIndexId?: string;
    subsectionIndexId?: string;
    coverageIndexId?: string;
    clauseIndexIds: string[];

    index: number;
    segmentIndex: number;
    constructor(data: any = {}) {
        this.clauseIndexId = data.clauseIndexId || 0;
        this.subsectionIndexId = data.subsectionIndexId || 0;
        this.coverageIndexId = data.coverageIndexId || 0;
        this.clauseIndexIds = data.clauseIndexIds || [];
        this.index = data.index || 0;
        this.segmentIndex = data.segmentIndex || 0;
    }
    match(identifier: Identifier) {
        if (identifier.type === 'Inner') {
            throw new Error('match identifier.type === Inner');
        }
        return (
            this.clauseIndexId === identifier.indexId ||
            this.subsectionIndexId === identifier.indexId ||
            this.coverageIndexId === identifier.indexId
        );
    }
    fromIdentifier(identifier: Identifier) {
        if (identifier.type === 'Clause') {
            this.clauseIndexId = identifier.indexId;
        }
        if (identifier.type === 'Coverage') {
            this.coverageIndexId = identifier.indexId;
        }
        if (identifier.type === 'Subsection') {
            this.subsectionIndexId = identifier.indexId;
        }
        return this;
    }
    equals(other?: Segment) {
        if (!other) {
            return false;
        }
        return (
            this.clauseIndexId === other.clauseIndexId &&
            this.subsectionIndexId === other.subsectionIndexId &&
            this.coverageIndexId === other.coverageIndexId &&
            this.clauseIndexIds === other.clauseIndexIds
        );
    }
    getClauses() {
        return this.clauseIndexIds;
    }
    setClauses(clauseIndexIds: string[]) {
        this.clauseIndexIds = clauseIndexIds;
        return this.clauseIndexIds;
    }

    toPlainObject() {
        return {
            subsectionIndexId: this.subsectionIndexId || null,
            coverageIndexId: this.coverageIndexId || null,
            clauseIndexId: this.clauseIndexId || null,
            clauseIndexIds: this.clauseIndexIds,
        };
    }
}

export interface ContractSection {
    sectionIndexId: string;
    segments: Segment[];
    active?: boolean;
}
export class Contract extends Base {
    __type: string = 'Contract';
    markets: Market[];
    contractSections: ContractSection[];
    productTypeIndexId: string;
    constructor(data: any = {}) {
        super(data);
        this.markets = data.markets ? [...data.markets] : [];
        this.contractSections = (data.contractSections || []).map((rawCS: any) => {
            return { ...rawCS, segments: (rawCS?.segments || []).map((s: any) => new Segment(s)) };
        });
        this.productTypeIndexId = data.productTypeIndexId;
    }
    getIndexIds() {
        return this.contractSections
            .map((contractSection) => [
                contractSection.sectionIndexId,
                ...contractSection.segments.map((segment) => [
                    segment.clauseIndexId || segment.coverageIndexId || segment.subsectionIndexId,
                    ...segment.clauseIndexIds,
                ]),
            ])
            .flat(2)
            .filter((indexId) => indexId) as string[];
    }
    sectionsEqual(other?: Contract) {
        if (!other) {
            return false;
        }
        return !this.contractSections.find((contractSection, i) => {
            const otherContractSection = other.contractSections[i];
            if (!otherContractSection) {
                return true;
            }
            if (contractSection.sectionIndexId !== otherContractSection.sectionIndexId) {
                return true;
            }
            if (contractSection.segments.length !== otherContractSection.segments.length) {
                return true;
            }
            const segmentWithoutMatch = contractSection.segments.find((segment, i) => {
                const otherSegment = otherContractSection.segments[i];
                if (!otherSegment) {
                    return true;
                }
                if (segment.clauseIndexId) {
                    return segment.clauseIndexId !== otherSegment.clauseIndexId;
                }
                if (
                    segment.clauseIndexIds.find((clauseIndexId, ci) => {
                        return clauseIndexId !== otherSegment.clauseIndexIds[ci];
                    })
                ) {
                    return true;
                }
                if (segment.subsectionIndexId) {
                    return segment.subsectionIndexId !== otherSegment.subsectionIndexId;
                }
                if (segment.coverageIndexId) {
                    return segment.coverageIndexId !== otherSegment.coverageIndexId;
                }
                return false;
            });
            if (segmentWithoutMatch) {
                return true;
            }
            return false;
        });
    }
    equals(other?: Contract) {
        if (!other) {
            return false;
        }
        if (this.name !== other.name) {
            return false;
        }
        if (this.productTypeIndexId !== other.productTypeIndexId) {
            return false;
        }
        if (!this.sectionsEqual(other)) {
            return false;
        }
        if (
            JSON.stringify(this.markets.map((m) => `${m.countryIndexId}-${m.languageIndexId}`)) !==
            JSON.stringify(other.markets.map((m) => `${m.countryIndexId}-${m.languageIndexId}`))
        ) {
            return false;
        }
        return true;
    }
    toPlainObject() {
        const { name, markets, productType, contractSections } = this;
        return { name, markets, productType, contractSections };
    }
    toFirestore() {
        const { markets, contractSections, productTypeIndexId } = this;
        return {
            ...this.baseToFirestore(),
            markets,
            contractSections: contractSections
                .filter((contractSection) => contractSection.active)
                .map((contractSection) => {
                    return {
                        sectionIndexId: contractSection.sectionIndexId,
                        segments: contractSection.segments.map((segment) =>
                            segment.toPlainObject()
                        ),
                        active: true,
                    };
                }),
            productTypeIndexId,
        };
    }
}

export const getTranslationKey = ({
    targetType,
    targetField,
    targetId,
}: {
    targetType: Translatable;
    targetField: 'name' | 'content';
    targetId: string;
}) => `${targetType}-${targetId}-${targetField}`;

export type Translatable = 'Clause' | 'Section' | 'Subsection' | 'Coverage';
export type TranslationTarget = Clause | Section | Subsection | Coverage;
export class Translation extends Base {
    __type: string = 'Translation';
    tags: string[];
    targetType: Translatable;
    targetField: 'name' | 'content';
    targetId: string;
    languageIndexId: string;
    content: string;
    constructor(data: any = {}) {
        super(data);
        this.tags = data.tags;
        this.targetType = data.targetType;
        this.targetField = data.targetField;
        this.targetId = data.targetId;
        this.languageIndexId = data.languageIndexId;
        this.content = data.content || null;
        this.name = data.name || '';
    }
    makeTags() {
        const tags = [
            `tenantId-${this.tenantId}`,
            `targetType-${this.targetType}`,
            `targetField-${this.targetField}`,
            `languageIndexId-${this.languageIndexId}`,
            `languageIndexId-${this.targetType}-${this.languageIndexId}`,
        ];
        const emptyTag = tags.find((tag) => tag.split('-').length < 2);
        if (emptyTag) {
            throw new Error(`Empty tag ${emptyTag} not allowed in ${JSON.stringify(this)}`);
        }
        return tags;
    }
    toFirestore() {
        return {
            ...this.baseToFirestore(),
            tags: this.makeTags(),
        };
    }
}

export type CommitAction = 'add' | 'update' | 'delete' | 'review';
export class Commit extends Base {
    __type: string = 'Commit';
    message: string;
    action: CommitAction;
    createdBy: string;
    targetIndexId: string;
    languageIndexId: string | null;
    typeIndex: string;
    changes: Change[];
    constructor(data: any = {}) {
        super(data);
        this.message = data.message || '';
        this.action = data.action;
        this.createdBy = data.createdBy;
        this.targetIndexId = data.targetIndexId;
        this.typeIndex = data.typeIndex;
        this.languageIndexId = data.languageIndexId || null;
        this.changes = data.changes || [];
    }
    show() {
        return (
            this.changes.filter(
                (change) =>
                    change.before || change.beforeValues || change.after || change.afterValues
            ).length > 0
        );
    }
    toFirestore() {
        const { message, action, createdBy, targetIndexId, languageIndexId, typeIndex, changes } =
            this;
        return {
            ...this.baseToFirestore(),
            message,
            action,
            createdBy,
            targetIndexId,
            languageIndexId,
            typeIndex,
            changes,
            createdAt: serverTimestamp(),
        };
    }
}

type StorageFieldVersion = 'v0';

export interface StorageField {
    path: string;
    fileType: string;
    version: StorageFieldVersion;
}

export class Upload extends Base {
    __type = 'Upload';
    storage: StorageField;
    createdBy: string;
    uploadFilename: string;
    constructor(data: any = {}) {
        super(data);
        this.storage = data.storage;
        this.createdBy = data.createdBy;
        this.uploadFilename = data.uploadFilename;
    }
    toFirestore() {
        const { storage, uploadFilename, createdBy } = this;
        return { ...this.baseToFirestore(), storage, uploadFilename, createdBy };
    }
}

type ImageFieldVersion = 'v0';
export interface ImageField extends StorageField {
    height: string;
    width: string;
    version: ImageFieldVersion;
}

export class UserProfile extends Base {
    __type = 'UserProfile';
    email: string;
    title: string;
    roles: string[];
    profileImages: ImageField[];
    constructor(data: any = {}) {
        super(data);
        this.email = data.email || '';
        this.title = data.title || '';
        this.roles = data.roles || [];
        this.profileImages = data.profileImages || [];
        this.name = this.name || '';
    }
    diff(previous: Base) {
        if (this.email !== previous.email) {
            throw new Error('not allowed to change email');
        }
        return this.baseDiff(previous)
            .concat(this.makeChanges(previous, ['title']))
            .concat(this.makeArrayChanges(previous, ['roles', 'profileImages']));
    }
    toFirestore() {
        const { profileImages, email, title, roles } = this;
        return {
            ...this.baseToFirestore(),
            profileImages,
            email,
            roles,
            title,
        };
    }
    admin() {
        return !!this.roles.find((role) => role === 'admin');
    }
}

export class Account extends Base {
    __type = 'Account';
    markets: Market[];
    constructor(data: any = {}) {
        super(data);
        this.markets = data.markets || [];
    }
    getMarkets(): Market[] {
        return this.markets.map((m) => {
            return { ...m, main: true };
        });
    }
    toFirestore() {
        const { markets } = this;
        return { ...this.baseToFirestore(), markets };
    }
}
export interface InputListItem {
    id: string;
    name: string;
}
export interface Column {
    field: string;
    fieldType?: 'boolean' | 'createdAt' | 'subset' | 'reviewStatus';
    label: string;
    filter?: boolean;
    column?: string;
    input?: boolean;
    selected?: boolean;
    index?: string;
    direction?: OrderByDirection;
    required?: boolean;
    source?: string;
    sourceSet?: InputListItem[];
}

const getConverter = <T extends Base>(getNew: (_: any) => T) => {
    return {
        toFirestore(data: T): DocumentData {
            return getNew(data).toFirestore();
        },
        fromFirestore(snapshot: QueryDocumentSnapshot): T {
            return getNew({ ...snapshot.data(), id: snapshot.id });
        },
    };
};

export const productTypeConverter = getConverter((data: any) => new ProductType(data));
export const PRODUCT_TYPE_COLLECTION_NAME = 'productTypeV1';

export const languageConverter = getConverter((data: any) => new Language(data));
export const LANGUAGE_COLLECTION_NAME = 'languageV1';

export const countryConverter = getConverter((data: any) => new Country(data));
export const COUNTRY_COLLECTION_NAME = 'countryV1';

export const subsectionConverter = getConverter((data: any) => new Subsection(data));
export const SUBSECTION_COLLECTION_NAME = 'subsectionV1';

export const sectionConverter = getConverter((data: any) => new Section(data));
export const SECTION_COLLECTION_NAME = 'sectionV1';

export const clauseConverter = getConverter((data: any) => new Clause(data));
export const CLAUSE_COLLECTION_NAME = 'clauseV1';

export const contractConverter = getConverter((data: any) => new Contract(data));
export const CONTRACT_COLLECTION_NAME = 'contractV1';

export const coverageConverter = getConverter((data: any) => new Coverage(data));
export const COVERAGE_COLLECTION_NAME = 'coverageV1';

export const translationCoverter = getConverter((data: any) => new Translation(data));
export const TRANSLATIONS_COLLECTION_NAME = 'translationV1';

export const userProfileCoverter = getConverter((data: any) => new UserProfile(data));
export const USER_PROFILE_COLLECTION_NAME = 'userProfileV0';

export const commitCoverter = getConverter((data: any) => new Commit(data));
export const COMMIT_COLLECTION_NAME = 'commitV0';

export const accountConverter = getConverter((data: any) => new Account(data));
export const ACCOUNT_COLLECTION_NAME = 'accountV0';

export const uploadConverter = getConverter((data: any) => new Upload(data));
export const UPLOAD_COLLECTION_NAME = 'uploadV0';

export interface ContextProviderProps<T> {
    constraints?: QueryConstraint[];
    collectionName: string;
    converter: FirestoreDataConverter<T>;
    keyFunction?: (_: T) => string;
}

export interface ProviderProps<T = Base> {
    converter: FirestoreDataConverter<T>;
    collectionName: string;
}
export const getProviderProps = (target: Translatable): ProviderProps => {
    if (target === 'Clause') {
        return {
            converter: clauseConverter,
            collectionName: CLAUSE_COLLECTION_NAME,
        };
    }
    if (target === 'Section') {
        return {
            converter: sectionConverter,
            collectionName: SECTION_COLLECTION_NAME,
        };
    }
    if (target === 'Subsection') {
        return {
            converter: subsectionConverter,
            collectionName: SUBSECTION_COLLECTION_NAME,
        };
    }
    if (target === 'Coverage') {
        return {
            converter: coverageConverter,
            collectionName: COVERAGE_COLLECTION_NAME,
        };
    }
    throw new Error(`unknown target '${target}'`);
};
